/**
 * @fileoverview The one to-do list's writes (D51): ticking a job or some of
 * its plants, taking a tick back, doing a job today or postponing it,
 * putting it under When you can, skipping it, bringing back the jobs
 * postponed, changing the day a planting job was done, and deleting a
 * gardener's own to-do or taking the delete back (D50: remove, unremove;
 * adding and editing one are api/ownTodo). The server finds
 * the job by its key (base|day) and does the work (notes, stage notes, its
 * history); it answers as soon as the mark is saved, with what to say about
 * it (said: the toast's words and its Undo; the to-do rebuild, batch 5: the
 * app chose these itself before) and no brief (B49: the brief built for the
 * answer took past the app's limit on a slow server); the page reads the
 * brief again on its own (useTodoMarks).
 *
 * The garden's postcode and day are the server's (the garden being tended,
 * its own clock): the mark sends only how the brief was asked for (language,
 * unit, Coming up's days).
 *
 * Where: features/today/lib/useTodoMarks (Today, the top of Plants, Beds).
 * API: POST /todo/marks { key, action, plantIds?, day?, lang, unit, days, device } (an own to-do's key for remove and unremove); for advice (markRead) { key, action:
 * seen|put_away|done } or { action: done, event }, answered with the person's advice afresh.
 *
 * @module api/todo
 */

import { apiClient } from './apiClient';
import type { BriefReads, Device, ReadEvent } from './todayBrief';

/** What a mark does; remove and unremove delete a gardener's own to-do and take the delete back (D50). */
export type TodoAction = 'done' | 'undone' | 'today' | 'tomorrow' | 'later' | 'whenever' | 'unpark' | 'skip' | 'unskip' | 'back' | 'day' | 'remove' | 'unremove';

/** One mark to make. */
export interface TodoMark {
  /** The job's key; left out to bring back every job postponed. */
  key?: string;
  action: TodoAction;
  /** Some of the job's plants, for done and undone; none for the whole job. */
  plantIds?: number[];
  /** The day a planting job was done, for action day; the day a job is postponed to, for action later ("YYYY-MM-DD"). */
  day?: string;
  /** D69: the bed the job's Seed vault plants went into (its planting.askBed), for done on Sow outside or Plant out. */
  bedId?: number;
}

/** How the brief a mark answers with is asked for: as the page asked for its own. */
export interface TodoAsk {
  lang: string;
  unit: 'F' | 'C';
  /** Coming up's days; null when the page did not say (the server uses the gardener's own or the Grow setting's). */
  days: number | null;
  /** The device the app is on, for the advice the answer carries. */
  device: Device;
}

/** What the server says after a mark: the toast's words, and the mark its Undo makes (with its words); null for none. */
export interface TodoSaid {
  toast: string | null;
  undo: { action: TodoAction; key: string; text: string } | null;
  /** A skip's line, as the brief will say it under the job ("Skipped. Grow won't ask about this one again."; B49); absent for any other mark. */
  line?: string | null;
}

/** A mark's answer (B49): saved, and what to say; no brief (the page reads it again). */
export interface TodoMarkAnswer {
  success: boolean;
  /** The mark is saved. */
  saved?: boolean;
  said?: TodoSaid;
}

/**
 * Makes a mark; answers once it is saved, with what to say.
 *
 * @param mark - What to do, to which job.
 * @param ask - How the brief was asked for (language, unit, Coming up's days; the words of said are in its language).
 * @returns The answer: saved, with said.
 */
export async function markTodo(mark: TodoMark, ask: TodoAsk): Promise<TodoMarkAnswer> {
  const response = await apiClient.post<TodoMarkAnswer>('/todo/marks', { ...mark, lang: ask.lang, unit: ask.unit, days: ask.days, device: ask.device });
  return response.data;
}

/** A mark on a piece of advice: seen (once a day), put away, or its button pressed (done); or an event that finishes advice. */
export type ReadMark = { key: string; action: 'seen' | 'put_away' | 'done' } | { action: 'done'; event: ReadEvent };

/**
 * Marks a piece of advice, with or without a postcode, and answers with the person's advice afresh.
 *
 * @param mark - The advice's key and what happened to it, or the event that happened.
 * @param ask - How the brief was asked for (its language and device; its unit and days when there is one).
 * @returns The advice for each place now, and the events still waited for.
 */
export async function markRead(mark: ReadMark, ask: Partial<TodoAsk> & { lang: string; device: Device }): Promise<BriefReads> {
  const response = await apiClient.post<BriefReads>('/todo/marks', { ...mark, lang: ask.lang, unit: ask.unit, days: ask.days, device: ask.device });
  return response.data;
}
